import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSmartTimesheetContext } from '../SmartTimesheetContextProvider';
import { SmartDiaryEntry } from '../type';

export const SD_SELECTION_KEY = 'sd_selections';

const getItem = () => {
  try {
    const sdSelection = localStorage.getItem(SD_SELECTION_KEY);
    return JSON.parse(sdSelection ?? '{}');
  } catch {
    return {};
  }
};

export const useSmartEntriesSelectionState = (
  smartEntries: SmartDiaryEntry[]
): void => {
  const entryIds = useMemo(
    () =>
      smartEntries
        .filter((entry) => entry.canBeSynced)
        .map((entry) => entry.id),
    [smartEntries]
  );
  const { control, getValues } = useFormContext();
  const defaultValue = useMemo(() => {
    const values = getValues();
    return entryIds.reduce((acc, key) => {
      acc[key] = Boolean(values[key] ?? false);
      return acc;
    }, {} as Record<string, boolean>);
  }, [entryIds, getValues]);

  const formValues = useWatch({
    name: entryIds,
    control,
    defaultValue
  });

  const values = useMemo<Record<string, boolean>>(
    () =>
      entryIds.reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [formValues, entryIds]
  );

  const updateStoredValue = useCallback(() => {
    const storedValue = getItem();
    localStorage.setItem(
      SD_SELECTION_KEY,
      JSON.stringify({
        ...storedValue,
        ...values
      })
    );
  }, [values]);
  useEffect(() => {
    window.addEventListener('beforeunload', updateStoredValue);
    return () => {
      window.removeEventListener('beforeunload', updateStoredValue);
      updateStoredValue();
    };
  });
};

export const getSmartEntriesSelection = (): Record<string, boolean> =>
  getItem();

export const useRemoveSmartEntriesSelection = (): {
  removeSmartEntriesSelection: (ids: string[]) => void;
} => {
  const { setSmartDiarySelectionUpdated } = useSmartTimesheetContext();
  const removeSmartEntriesSelection = (ids: string[]) => {
    const storedValue = getItem();
    const filterValue = Object.keys(storedValue).reduce((acc, key) => {
      if (ids.includes(key)) {
        return acc;
      }
      return { ...acc, [key]: storedValue[key] };
    }, {} as Record<string, boolean>);
    localStorage.setItem(SD_SELECTION_KEY, JSON.stringify(filterValue));
    setSmartDiarySelectionUpdated(true);
  };
  return {
    removeSmartEntriesSelection: removeSmartEntriesSelection
  };
};

export const clearSmartEntriesSelection = (): void => {
  localStorage.removeItem(SD_SELECTION_KEY);
};
